import React, { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { Badge } from "../components/ui/badge";
import { User, KeyRound, ImageUp, Loader2 } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const isBM = user?.role === "branch_manager";

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Ukuran logo maksimal 2MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setLogoUploading(true);
      try {
        await api.post("/settings/logo", { data_url: reader.result });
        toast.success("Logo berhasil diunggah");
        setTimeout(() => window.location.reload(), 800);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Gagal mengunggah logo");
      } finally { setLogoUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const change = async (e) => {
    e.preventDefault();
    if (nw !== confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    if (nw.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password berhasil diubah");
      setCur(""); setNw(""); setConfirm("");
      setUser({ ...user, must_change_password: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <Toaster position="top-center" richColors />
      <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Profil</h2>

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gtsorange to-gtsorange-light flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name || "?").charAt(0)}
          </div>
          <div>
            <p className="font-nunito font-bold text-xl text-gtsnavy">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <Badge className={`mt-1 rounded-full ${user?.role === "branch_manager" ? "bg-gtsnavy text-white" : "bg-orange-100 text-gtsorange"}`}>
              {user?.role === "branch_manager" ? "Branch Manager" : "Coordinator GTS"}
            </Badge>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-gtsorange" />
          <h3 className="font-nunito font-bold text-gtsnavy">Ganti Password</h3>
        </div>
        <form onSubmit={change} className="space-y-4">
          <div><Label>Password Lama</Label><PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-xl mt-1" testid="current-pw" /></div>
          <div><Label>Password Baru</Label><PasswordInput value={nw} onChange={(e) => setNw(e.target.value)} className="rounded-xl mt-1" testid="new-pw" /></div>
          <div><Label>Konfirmasi Password Baru</Label><PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-xl mt-1" testid="confirm-pw" /></div>
          <Button type="submit" disabled={loading} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white" data-testid="change-pw-submit">
            {loading ? "Menyimpan..." : "Ubah Password"}
          </Button>
        </form>
      </Card>

      {isBM && (
        <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white" data-testid="logo-upload-card">
          <div className="flex items-center gap-2 mb-4">
            <ImageUp className="w-5 h-5 text-gtsorange" />
            <h3 className="font-nunito font-bold text-gtsnavy">Logo IEC Mustikajaya</h3>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-gtscream border flex items-center justify-center overflow-hidden shrink-0">
              <LogoPreview />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-2">Logo tampil di sidebar dan header. Format PNG/JPG, maks 2MB.</p>
              <label className="inline-flex items-center gap-2 rounded-full bg-gtsnavy text-white px-4 py-2 text-sm font-medium cursor-pointer hover:bg-gtsnavy-hover transition-colors" data-testid="logo-upload-label">
                {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageUp className="w-4 h-4" />}
                Unggah Logo
                <input type="file" accept="image/*" className="hidden" onChange={onLogoChange} data-testid="logo-input" />
              </label>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function LogoPreview() {
  const [url, setUrl] = useState(null);
  React.useEffect(() => {
    api.get("/settings/branding").then((r) => setUrl(r.data?.logo_url)).catch(() => {});
  }, []);
  return url ? <img src={url} alt="logo" className="w-full h-full object-contain p-1" data-testid="logo-preview" /> : <span className="text-xs text-muted-foreground">No logo</span>;
}
